import { useDispatch, useSelector , useStore} from 'react-redux';
import { addMessage, setTranscript, toggleLoader , setHistoryPointer} from '../store/actions';
import * as viewerUtils from '../utils/viewerUtils';
import { storageKeys } from '../constants/storageKeys';
import { useGenerateLLMResponse } from './useGenerateLLMResponse';
import { useBPMNRenderer } from './useBPMNRenderer';
import { useCheckers } from './useCheckers';
import { transcribeAudio } from '../services/transcribeAudio';

export const useChatActions = () => {

    const handleGenerateLLMResponse  = useGenerateLLMResponse();
    const renderBPMN  = useBPMNRenderer();
    const runCheckers = useCheckers();

    const dispatch = useDispatch();
    const viewer = useSelector((state) => state.viewer);
    const processedJson = useSelector((state) => state.processedJson);
    const transcript = useSelector((state) => state.transcript);
    const historyPointer = useSelector(state => state.history.pointer);

    const store = useStore();


    /**
     * Processes the user's transcript input:
     * - Validates the transcript is not empty.
     * - Dispatches the user's message to the chat.
     * - Clears the transcript input.
     * - Generates an LLM response and renders the BPMN diagram.
     * - Notifies the user to check the diagram and suggests using model checkers.
     * - Handles and reports any errors during the process.
     *
     * @param {string} transcript - The user's process description input.
     */
    const processUserTranscript = async (transcript) => {
        try {
          if (transcript.trim() === '') {
            dispatch(addMessage({ sender: 'assistant', content: "Please enter your process description." }));
            throw new Error("I encountered an error: Empty Transcript.");
          }
    
          dispatch(addMessage({ sender: 'user', content: transcript }));
          dispatch(setTranscript(""));

    
          await handleGenerateLLMResponse(transcript);
          await renderBPMN();

          // Get latest history pointer from state
          const latestHistoryPointer = store.getState().history.pointer;
          dispatch(addMessage({ 
            sender: 'assistant', 
            content: `Your process model is ready! <br/> <span class='go-to-model-diagram' data-id='${latestHistoryPointer}'>[View Diagram]</span>` 
          }));
          dispatch(addMessage( {
            "sender": "assistant",
            "content": "🛠️ You can use the model checkers to inspect the current model for possible issues.<br/><span class='check-me'>[Click Here]</span>"
          }));
    
          /*
          dispatch(addMessage({
            sender: 'assistant',
            content: "Before you end the chat, we’d love your help! Please consent to share your chat data for research and participate in a quick survey. <span class='consent-here'>[Click Here]</span>"
          }));*/
    
        } catch (error) {
          if (
            !(
              (error && error.name === 'AbortError') ||
              (error && error.code === 'ERR_CANCELED')
            )
          ) {
            dispatch(addMessage({ sender: 'assistant', content: `I had a problem generating or rendering your process model: ${error}` }));
          }
        }
    };
    
    /**
     * Handles the submit event for the transcript input.
     * - Processes the user's transcript input. on click or Enter (without Shift).
     *
     * @param {Event} e - The event object from the submit action.
     */
    const handleSubmit = async (e) => {
        if ((e.type === 'click' || e.key === 'Enter') && !e.shiftKey) {
          e.preventDefault(); 
          await processUserTranscript(transcript);     
        }
    };

    /**
     * Starts the audio recorder and toggles the recorder loader in the UI.
     * If starting the recorder fails, notifies the user with an assistant message.
     *
     * @param {Object} recorder - The recorder instance to start.
     */
    const handleStartRecording = (recorder) => {   
        recorder
          .start()
          .then(() => {
            dispatch(toggleLoader("recorder", true));
          })
          .catch((error) => {
            console.error(error);
            dispatch(toggleLoader("recorder", false));
            dispatch(addMessage({ sender: 'assistant', content: 'I have a problem with the recorder. Please use keyboard text input.' }));
          });
    };
    
    /**
     * Handles stopping the audio recorder, transcribing the audio, and processing the transcript.
     * - Stops the recorder and retrieves the audio buffer and blob.
     * - Transcribes the audio to text.
     * - Updates the UI with the transcript and processes it as user input.
     * - Handles errors by notifying the user and clearing the transcript.
     *
     * @param {Object} recorder - The recorder instance to stop and process.
     */
    const handleStopRecording = async (recorder) => {
      dispatch(toggleLoader("recorder", false));
      try {
          const  [buffer, blob] = await recorder.stop().getMp3();
          if (buffer && blob) {
            dispatch(toggleLoader("user", true));
            const transcript = await transcribeAudio(buffer, blob);
            dispatch(toggleLoader("user", false));
            dispatch(setTranscript(transcript));
            processUserTranscript(transcript);
          } else {
            throw new Error('I encountered an error: Recording stopped but buffer or blob is null.');    
          }
        } catch (error) {
          console.error(error);
          dispatch(toggleLoader("user", false));
          dispatch(toggleLoader("recorder", false));
          dispatch(setTranscript(""));
          dispatch(addMessage({ sender: 'assistant', content: 'I have a problem with your audio. Please try again or use keyboard input.' }));
        }
    };


    /*************** Checker Response Action Handlers ***************/
  
    /**
     * Handles running all selected BPMN checkers on the current diagram.
     * - Extracts the BPMN XML from the viewer.
     * - Passes the XML and processed BPMN JSON to the runCheckers hook.
     * - Handles errors if the viewer is unavailable or XML extraction fails.
     */
    const handleRunCheckers = async () => {
      if (!viewer) {
        console.error('Viewer is not available');
        return;
      }
  
      try {
        const { xml } = await viewer.saveXML({ format: true });
        await runCheckers(xml, processedJson); // Pass XML to the hook

      } catch (error) {
        console.error('Error extracting XML from viewer:', error);
      }
    };

    /**
     * Retrieves a checker result object from sessionStorage based on the data-id attribute of the event target.
     * 
     * @param {Event} e - The event object containing the target element with a data-id attribute.
     * @returns {Object|undefined} - The checker result object corresponding to the data-id, or undefined if not found.
     */
    const getCheckerResult = (e) => {
        const checkerResultId = e.target.getAttribute('data-id');
        const checkerResultsMapping = JSON.parse(sessionStorage.getItem(storageKeys.CHECKERS_RESULTS_MAPPING)) || {};
        return checkerResultsMapping[checkerResultId];
    };

    /**
     * Handles copying the checker result message to the clipboard.
     * Retrieves the checker result object using the event, and copies its message to the clipboard.
     * If the checker result is not found or copying fails, logs a warning.
     *
     * @param {Event} e - The event object containing the target element with a data-id attribute.
     */
    const handleCopyRequest = (e) => {
      try {
        const checkerResult = getCheckerResult(e);
        if (!checkerResult) return;
        navigator.clipboard.writeText(checkerResult.message);
      } catch (error) {
        console.warn("Copy Failed.");
      }
    };
  
    /**
     * Handles the autofix request for a checker result.
     * - Retrieves the checker result object using the event.
     * - Determines the autofix message to use (prefers autofixMessage if available, otherwise falls back to message).
     * - Dispatches the autofix message as a user message.
     * - Calls the LLM to generate a response using the autofix message and optional prompt instructions.
     * - Renders the updated BPMN diagram.
     * - Notifies the user that the issue has been fixed and suggests running the model checkers again.
     * - Handles and reports any errors that occur during the process.
     *
     * @param {Event} e - The event object containing the target element with a data-id attribute.
     */
    const handleAutofixRequest  = async (e) => {
      const dataPointer = e.target.getAttribute('data-pointer');
      if (dataPointer && dataPointer !== String(historyPointer)) {
        dispatch(addMessage({
          sender: 'assistant',
          content: `
            <div>
              ⚠️ The model has changed since this issue was detected. Autofix will not be run.<br/>
              <span>
                You can call the model checkers again.<br/>
                <span class='check-me'>[Click Here]</span>
              </span>
            </div>
          `
        }));
        return;
      }
      try {
        const checkerResult = getCheckerResult(e);
        if (!checkerResult) return;

        let autofixMessage;

        if(checkerResult.autofixMessage && checkerResult.autofixMessage.length > 0) {
          autofixMessage = checkerResult.autofixMessage;
        } else{
          autofixMessage = checkerResult.message;
        }
        
        dispatch(addMessage({ sender: 'user', content: autofixMessage }));

        await handleGenerateLLMResponse(
          autofixMessage.replace(/<br\/?>/g, '\n'), // remove <br/> tags
          checkerResult.promptInstructions ?? null
        );
        
        await renderBPMN();
        const latestHistoryPointer = store.getState().history.pointer;
        dispatch(addMessage({ sender: 'assistant', 
          content: `The issue has been resolved! <br/>
          <span class='go-to-model-diagram' data-id='${latestHistoryPointer}'>[View Diagram]</span>` }));
        dispatch(addMessage( {
            "sender": "assistant",
            "content": "🛠️ You can call the model checkers again.<br/><span class='check-me'>[Click Here]</span>"
        }));
        
      } catch (error) {
        dispatch(addMessage({ sender: 'assistant', content: `I had a problem processing your request: ${error}` }));
      }
    };
  
    /**
     * Handles highlighting the path(s) associated with a checker result in the BPMN viewer.
     * - Retrieves the checker result object using the event.
     * - Clears any existing highlights in the viewer.
     * - Determines the highlight color based on the result's classification.
     * - Highlights each element in the result's path(s) with the appropriate color.
     * - Handles and reports any errors that occur during the process.
     *
     * @param {Event} e - The event object containing the target element with a data-id attribute.
     */
    const handleHighlightPath = async (e) => {
      const dataPointer = e.target.getAttribute('data-pointer');
      if (dataPointer && dataPointer !== String(historyPointer)) {
        dispatch(addMessage({
          sender: 'assistant',
          content: `
          <div>
            ⚠️ The model has changed since this issue was detected. Highlight will not be run.<br/>
            <span>
              You can call the model checkers again.<br/>
              <span class='check-me'>[Click Here]</span>
            </span>
          </div>
        `
        }));
        return;
      }
      try {
        const checkerResult = getCheckerResult(e);
        if (!checkerResult) return;
    
        viewerUtils.clearHighlights(viewer);
    
        const classificationColors = {
          "ERROR": 'red',
          "WARNING": 'orange',
          "PATTERN-ERROR": 'red',    
          "PATTERN-WARNING": 'orange',  
          "DEFAULT": 'black',
        };
    
        const color = classificationColors[checkerResult.classification] || classificationColors.DEFAULT;
    
        checkerResult.paths.flat().forEach((node) => {
          viewerUtils.highlightElement(node.elementId, color, viewer);
        });
      } catch (error) {
        dispatch(addMessage({ sender: 'assistant', content: `I had a problem processing your request: ${error}` }));
      }
    };


    const handleGoToModelDiagram = async (e) => {
      const historyPointer = e.target.getAttribute('data-id');
      dispatch(setHistoryPointer(historyPointer));
      await renderBPMN();
    };

  return { handleCopyRequest, handleAutofixRequest, handleHighlightPath, handleSubmit , handleStartRecording, handleStopRecording, handleRunCheckers, handleGoToModelDiagram};
};
